import { Bell } from 'lucide-react'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import '@/components/notifications/notification-bell.css'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { useNotificationPreferencesAccess } from '@/hooks/useNotificationPreferencesAccess'
import { useCoachingNotificationsEnabled, useNotifications, useUnreadNotificationCount } from '@/hooks/useNotifications'
import { useRole } from '@/hooks/useRole'
import { cn } from '@/lib/utils'
import type { AppNotification } from '@/types/notifications'
import { notificationDestination, notificationLines, surfaceForRole } from './notificationWording'

type Props = {
  readonly className?: string
  readonly onOpenSettings?: () => void
}

/** Largest number the badge prints before it stops counting. */
const BADGE_CAP = 99

/**
 * The notification bell, its unread badge and its list (ENG-3382).
 *
 * One component and one placement per shell. The only thing that varies is which
 * side of the relationship the reader is on, and that follows their **role**
 * rather than the shell: the learner header is shared, so a coach reading it
 * there is still a coach and still belongs on the coach screen.
 *
 * Hidden unless the reader has a coaching surface, and the hooks make no request
 * either, so somebody without one neither sees a bell nor polls for it. See
 * `useCoachingNotificationsEnabled` for why that is both flags rather than the
 * coach one.
 *
 * Three rules it lives by:
 *
 * 1. Opening an entry navigates and marks it read together, so the badge and the
 *    thread can never disagree.
 * 2. Unread is carried by wash, edge, dot and weight at once, never by colour
 *    alone. Read entries stay legible: they are the record.
 * 3. Every dated entry names its zone, and a reminder says "tomorrow" rather
 *    than a precise minute the 30-second dispatcher tick cannot promise.
 */
export function NotificationBell({ className, onOpenSettings }: Props) {
  const { t, i18n } = useTranslation()
  const { t: tPreferences } = useTranslation('notificationPreferences')
  const navigate = useNavigate()
  const flagEnabled = useCoachingNotificationsEnabled()
  const { userRole } = useRole()
  const showNotificationPreferences = useNotificationPreferencesAccess()
  const surface = surfaceForRole(userRole)
  const [open, setOpen] = useState(false)

  const { unread, refresh: refreshCount } = useUnreadNotificationCount()
  const { notifications, hasMore, isLoading, loadMore, markRead } = useNotifications(open)

  const unreadIds = useMemo(
    () => notifications.filter((notification) => notification.read_at === null).map((n) => n.id),
    [notifications]
  )

  const handleOpen = useCallback(
    async (nextOpen: boolean) => {
      setOpen(nextOpen)
      // Reconciled on close, not on open: opening the panel is not the same as
      // having dealt with what is in it.
      if (!nextOpen) await refreshCount()
    },
    [refreshCount]
  )

  const handleSelect = useCallback(
    async (notification: AppNotification) => {
      const destination = notificationDestination(notification, surface)
      // Marking read must not be able to swallow the navigation. Opening the
      // subject clears its notifications server-side anyway (ENG-3381), so a
      // failed mark-read costs a badge that corrects itself on the next poll,
      // whereas a click that goes nowhere reads as the bell being broken.
      if (notification.read_at === null) {
        try {
          await markRead([notification.id])
          await refreshCount()
        } catch {
          // Deliberately swallowed: the navigation below is what the reader asked
          // for, and the server clears on the read it is about to do.
        }
      }
      if (destination) {
        setOpen(false)
        navigate(destination)
      }
    },
    [markRead, navigate, refreshCount, surface]
  )

  const handleMarkAllRead = useCallback(async () => {
    await markRead(unreadIds)
    await refreshCount()
  }, [markRead, refreshCount, unreadIds])

  const showSettingsLink = showNotificationPreferences && onOpenSettings !== undefined

  if (!flagEnabled) return null

  return (
    <Popover open={open} onOpenChange={handleOpen}>
      <PopoverTrigger asChild>
        <button
          type='button'
          className={cn('notif relative rounded-md p-2 text-[color:var(--notif-ink)] hover:bg-gray-100', className)}
          aria-label={unread > 0 ? t('notifications.openWithUnread', { count: unread }) : t('notifications.open')}
        >
          <Bell size={21} strokeWidth={1.9} aria-hidden='true' />
          {/* The count is already in the button's accessible name, so the badge
              is decoration and announcing it twice is noise. */}
          {unread > 0 && (
            <span className='notif-badge' aria-hidden='true'>
              {unread > BADGE_CAP ? `${BADGE_CAP}+` : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>

      <PopoverContent
        // The two shells put the bell at opposite edges: the learner's is top
        // right of a header, the coach's is top left of a sidebar. Anchoring both
        // the same way runs one of them off the screen.
        align={surface === 'coach' ? 'start' : 'end'}
        collisionPadding={12}
        className='notif w-[min(25rem,var(--radix-popover-content-available-width))] overflow-hidden rounded-[14px] border-[color:var(--notif-edge)] p-0'
      >
        <div className='flex items-center gap-2.5 border-b border-[color:var(--notif-line)] px-4 py-3.5'>
          <h2 className='font-poppins text-base font-bold text-[color:var(--notif-ink)]'>{t('notifications.title')}</h2>
          {unread > 0 && (
            <span className='inline-flex h-5 items-center rounded-[10px] bg-[color:var(--notif-accent)] px-2 text-[11.5px] font-bold text-white'>
              {unread > BADGE_CAP ? `${BADGE_CAP}+` : unread}
            </span>
          )}
          {unreadIds.length > 0 && (
            <button
              type='button'
              onClick={handleMarkAllRead}
              className='ml-auto text-[13px] font-semibold text-[color:var(--notif-accent)] hover:underline'
            >
              {t('notifications.markAllRead')}
            </button>
          )}
        </div>

        <div className='max-h-100 overflow-y-auto'>
          {isLoading && notifications.length === 0 && (
            <p className='px-5 py-8 text-center text-sm text-[color:var(--notif-ink-muted)]'>
              {t('notifications.loading')}
            </p>
          )}

          {/* Empty is the normal condition for someone who signs in every few
              weeks, so it reads as calm rather than as a failure. */}
          {!isLoading && notifications.length === 0 && (
            <div className='flex flex-col items-center gap-1.5 px-5 py-8 text-center'>
              <span className='text-sm font-bold text-[color:var(--notif-ink)]'>{t('notifications.emptyTitle')}</span>
              <span className='max-w-[270px] text-[13.5px] leading-relaxed text-[color:var(--notif-ink-muted)]'>
                {t('notifications.emptyDetail')}
              </span>
            </div>
          )}

          <ul>
            {notifications.map((notification) => {
              const unreadRow = notification.read_at === null
              const { title, detail } = notificationLines(t, notification, surface, i18n.language)
              return (
                <li key={notification.id}>
                  <button
                    type='button'
                    onClick={() => handleSelect(notification)}
                    className={cn(
                      'flex w-full gap-2.5 border-b border-[color:var(--notif-line)] px-4 py-3.5 text-left last:border-b-0',
                      unreadRow ? 'notif-row-unread' : 'hover:bg-gray-50'
                    )}
                  >
                    <span className='notif-dot' aria-hidden='true' />
                    <span className='flex flex-col gap-0.5'>
                      <span
                        className={cn(
                          'text-[14.5px]',
                          unreadRow
                            ? 'font-bold text-[color:var(--notif-ink)]'
                            : 'font-semibold text-[color:var(--notif-ink-read)]'
                        )}
                      >
                        {title}
                      </span>
                      {detail && (
                        <span
                          className={cn(
                            'text-[13.5px] leading-snug',
                            unreadRow ? 'text-[color:var(--notif-ink-soft)]' : 'text-[color:var(--notif-ink-muted)]'
                          )}
                        >
                          {detail}
                        </span>
                      )}
                      <time
                        dateTime={notification.created_at}
                        className={cn(
                          'text-[12.5px]',
                          unreadRow ? 'text-[color:var(--notif-ink-muted)]' : 'text-[color:var(--notif-ink-faint)]'
                        )}
                      >
                        {new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium', timeStyle: 'short' }).format(
                          new Date(notification.created_at)
                        )}
                      </time>
                    </span>
                  </button>
                </li>
              )
            })}
          </ul>

          {/* The panel shows ten and offers the rest rather than growing without
              bound. No "n of m": the list endpoint returns no total, on purpose,
              because counting a table that only grows on every page buys a number
              nobody reads. */}
          {hasMore && notifications.length > 0 && (
            <button
              type='button'
              onClick={loadMore}
              className='w-full px-4 py-3.5 text-center text-[13.5px] font-semibold text-[color:var(--notif-accent)] hover:underline'
            >
              {t('notifications.loadMore')}
            </button>
          )}
        </div>
        {showSettingsLink && (
          <div className='flex items-center justify-between gap-3 border-t border-[color:var(--notif-line)] px-4 py-3 text-[13px]'>
            <span className='text-[color:var(--notif-ink-muted)]'>
              {tPreferences('notificationPreferences.panelFooter.tooMany')}
            </span>
            <button
              type='button'
              onClick={() => {
                setOpen(false)
                onOpenSettings()
              }}
              className='shrink-0 font-semibold text-[color:var(--notif-accent)] hover:underline'
            >
              {tPreferences('notificationPreferences.panelFooter.settings')}
            </button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  )
}
